import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import type { AutocompleteRenderOptionState } from "@mui/material/Autocomplete";
import Typography from "@mui/material/Typography";
import { type FC, type HTMLAttributes } from "react";

import { PlatformIcons } from "~/components/UI/PlatformIcons";
import { ROUTES } from "~/constants/routing";
import { getSearchTitle } from "~/feature/search/components/SearchOption/helper";
import type { IAggregatedEvent } from "~/types/event";
import { formatValue } from "~/utils/formatValue";

import * as Styled from "./SearchOption.styles";

const SEARCH_PLATFORM_ICON_SIZE = 24;

interface ISearchOptionsProps {
  liProps: HTMLAttributes<HTMLLIElement>;
  option: IAggregatedEvent;
  state: AutocompleteRenderOptionState;
}

export const SearchOption: FC<ISearchOptionsProps> = ({
  liProps,
  option,
  state,
}) => {
  const shortFormattedVol = formatValue(option.volume || 0);
  const chancePercent =
    option.chance != null ? Math.round(option.chance * 100) : null;

  const url =
    option.resultType === "market" && option.marketId
      ? ROUTES.event.createURL(option.urlSlug || "") +
        `?market=${option.marketId}`
      : ROUTES.event.createURL(option.urlSlug || "");

  return (
    // data-testid: lets the production smoke suite target real result rows
    // rather than any listbox item (recent searches also render as list items).
    <li {...liProps} data-testid="search-option">
      <Styled.OptionLink to={url}>
        <Styled.Container>
          <Styled.Title component="span" variant="caption" sx={{ fontWeight: "500" }}>
            {getSearchTitle(option, state.inputValue)}
          </Styled.Title>
          {option.marketQuestion && option.title && option.marketQuestion !== option.title && (
            <Styled.EventTitle component="span" variant="caption">
              {option.title}
            </Styled.EventTitle>
          )}
          <Styled.Info>
            <PlatformIcons
              platforms={option.platforms}
              size={SEARCH_PLATFORM_ICON_SIZE}
            />
            <Styled.VolLine>
              <Typography color="gray" variant="caption">
                Vol:
              </Typography>
              <Typography component="strong" variant="caption" sx={{
                fontWeight: 700
              }}>
                {shortFormattedVol} Vol
              </Typography>
            </Styled.VolLine>
            {chancePercent != null && (
              <Styled.Chance>
                <Typography component="strong" variant="caption" sx={{
                  fontWeight: 700
                }}>
                  {chancePercent}%
                </Typography>
                <Typography variant="caption" color="gray">
                  {" "}chance
                </Typography>
              </Styled.Chance>
            )}
          </Styled.Info>
        </Styled.Container>
        <ArrowForwardIosIcon fontSize="small" color="disabled" />
      </Styled.OptionLink>
    </li>
  );
};
